import type { AttendanceHistoryFilters } from '../types/attendance';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Default history window is the current calendar month (date-only, no
 * device timezone conversion). Backend requires both fromDate and toDate.
 */
export function createDefaultAttendanceDateRange(now = new Date()): {
  fromDate: string;
  toDate: string;
} {
  const year = now.getFullYear();
  const month = now.getMonth();
  const fromDate = formatDateOnly(new Date(year, month, 1));
  const toDate = formatDateOnly(new Date(year, month + 1, 0));
  return { fromDate, toDate };
}

export function createDefaultAttendanceFilters(
  now = new Date(),
): AttendanceHistoryFilters {
  return createDefaultAttendanceDateRange(now);
}

export function isValidAttendanceDate(value: string): boolean {
  if (!DATE_ONLY.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

export function validateAttendanceDateRange(
  fromDate: string,
  toDate: string,
): string | null {
  if (!isValidAttendanceDate(fromDate)) {
    return 'From date must use YYYY-MM-DD format.';
  }
  if (!isValidAttendanceDate(toDate)) {
    return 'To date must use YYYY-MM-DD format.';
  }
  if (fromDate > toDate) {
    return 'The selected date range is invalid.';
  }
  return null;
}

export function normalizeAttendanceFilters(input: {
  fromDate?: string;
  toDate?: string;
  attendanceMode?: string;
  page?: number;
  pageSize?: number;
}): {
  filters: AttendanceHistoryFilters;
  page: number;
  pageSize: number;
} {
  const defaults = createDefaultAttendanceDateRange();
  const fromDate = input.fromDate?.trim() || defaults.fromDate;
  const toDate = input.toDate?.trim() || defaults.toDate;
  const rangeError = validateAttendanceDateRange(fromDate, toDate);
  if (rangeError) {
    throw Object.assign(new Error(rangeError), {
      code: 'INVALID_DATE_RANGE',
    });
  }

  const page = input.page ?? 1;
  const pageSize = input.pageSize ?? 20;
  if (!Number.isInteger(page) || page <= 0) {
    throw Object.assign(new Error('page must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!Number.isInteger(pageSize) || pageSize <= 0 || pageSize > 100) {
    throw Object.assign(new Error('pageSize must be between 1 and 100'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const attendanceMode = input.attendanceMode?.trim() || undefined;
  return {
    filters: {
      fromDate,
      toDate,
      ...(attendanceMode ? { attendanceMode } : {}),
    },
    page,
    pageSize,
  };
}

function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
